import React, { useMemo, useState } from 'react';
import ModernTable from '../../../../components/ModernTable/Table';
import { ColumnDef } from '@tanstack/react-table';
import useBdeForm, { BdeEodItem, BdeEodForm } from './useBdeForm';
import Modal from '../../../../components/Model';
import { Input, Textarea } from '../../../../components/Form';

const BdeForm: React.FC = () => {
  const {
    items,
    loading,
    form,
    setField,
    submit,
    beginEdit,
    remove,
    editingId,
    resetForm,
    page,
    totalPages,
    totalItems,
    onPageChange,
  } = useBdeForm();

  const [isOpen, setIsOpen] = useState(false);

  const openForCreate = () => {
    resetForm();
    setIsOpen(true);
  };

  const openForEdit = (row: BdeEodItem) => {
    beginEdit(row);
    setIsOpen(true);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const ok = await submit();
    if (ok) setIsOpen(false);
  };

  const columns: ColumnDef<BdeEodItem>[] = useMemo(() => [
    { header: 'Date', accessorKey: 'createdAt', cell: ({ row }) => new Date(row.original.createdAt || '').toLocaleDateString('en-GB', { day: '2-digit', month: 'short' }) },
    { header: 'Name', accessorKey: 'user', cell: ({ row }) => {
      const u = row.original.user as any;
      if (!u) return '-';
      if (typeof u === 'string') return u;
      return u?.name || '-';
    } },
    { header: 'Emails Sent', accessorKey: 'emailSent' },
    { header: 'Email Replies', accessorKey: 'emailReplies' },
    { header: 'Cold Calls Made', accessorKey: 'coldCallsMade' },
    { header: 'Call Conversations', accessorKey: 'callConversations' },
    { header: 'LinkedIn Replies', accessorKey: 'linkedinReplies' },
    { header: 'Meetings Scheduled', accessorKey: 'meetingsScheduled' },
    { header: 'Follow Ups Done', accessorKey: 'followUpsDone' },
    {
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex gap-2">
          <button className="text-blue-600" onClick={() => openForEdit(row.original)}>Edit</button>
          <button className="text-red-600" onClick={() => row.original._id && remove(row.original._id)}>Delete</button>
        </div>
      ),
    },
  ], []);

  return (
    <div className="p-4">
      <ModernTable<BdeEodItem>
        data={items}
        columns={columns}
        loading={loading}
        title="BDE EOD"
        currentPage={page}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onAdd={() => openForCreate()}
      />

      <Modal isOpen={isOpen} onClose={() => setIsOpen(false)} style="w-[720px] max-h-[90vh]">
        <h3 className="text-lg font-semibold mb-4">{editingId ? 'Edit' : 'Create'} EOD</h3>
        <form onSubmit={handleSubmit} className="grid grid-cols-2 gap-4">
          <Input name="emailSent" label="Emails Sent" type="number" value={String(form.emailSent)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('emailSent', Number(e.target.value))} />
          <Input name="emailReplies" label="Email Replies" type="number" value={String(form.emailReplies)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('emailReplies', Number(e.target.value))} />
          <Input name="coldCallsMade" label="Cold Calls Made" type="number" value={String(form.coldCallsMade)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('coldCallsMade', Number(e.target.value))} />
          <Input name="callConversations" label="Call Conversations" type="number" value={String(form.callConversations)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('callConversations', Number(e.target.value))} />
          <Input name="linkedinReplies" label="LinkedIn Replies" type="number" value={String(form.linkedinReplies)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('linkedinReplies', Number(e.target.value))} />
          <Input name="meetingsScheduled" label="Meetings Scheduled" type="number" value={String(form.meetingsScheduled)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('meetingsScheduled', Number(e.target.value))} />
          <Input name="followUpsDone" label="Follow Ups Done" type="number" value={String(form.followUpsDone)} onChange={(e: React.ChangeEvent<HTMLInputElement>) => setField('followUpsDone', Number(e.target.value))} />
          <div className="col-span-2">
            <Textarea name="notes" label="Notes" value={form.notes || ''} onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setField('notes', e.target.value)} />
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <button type="button" className="px-4 py-2 border rounded" onClick={() => setIsOpen(false)}>Cancel</button>
            <button type="submit" className="px-4 py-2 bg-blue-600 text-white rounded">Save</button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default BdeForm;
